import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Loader2, Shield, ShieldAlert, ShieldCheck, ShieldX, 
  Download, RefreshCw, History, FileJson, FileText,
  Database, Lock, Mail, Key, Users, HardDrive, Send
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { format } from "date-fns";
import eabLogoHorizontal from "@/assets/eab-logo-horizontal.png";

// EAB brand colors
const BRAND_ORANGE = "#F39001";
const BRAND_DARK_ORANGE = "#C94F13";
const BRAND_GREEN = "#8FCA3F";
const BRAND_DARK_GREEN = "#22891D";
const BRAND_LIGHT_BLUE = "#29A4E3";
const BRAND_DARK_BLUE = "#1C61E0";

interface Finding {
  id: string;
  severity: "critical" | "high" | "medium" | "low";
  category: string;
  title: string;
  evidence: Record<string, unknown>;
  recommendation: string;
}

interface ReportSummary {
  total_findings: number;
  by_severity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  by_category: Record<string, number>;
  risk_rating: "green" | "yellow" | "red";
  generated_at: string;
}

interface SecurityReport {
  id: string;
  created_at: string;
  created_by_admin_id: string;
  app_env: string;
  summary: ReportSummary;
  status: "success" | "failed" | "running";
}

const severityColors: Record<string, string> = {
  critical: "bg-red-500 text-white",
  high: "bg-orange-500 text-white",
  medium: "bg-yellow-500 text-black",
  low: "bg-blue-500 text-white",
};

const categoryIcons: Record<string, React.ReactNode> = {
  "RLS Coverage": <Database className="h-4 w-4" />,
  "PII Exposure": <Users className="h-4 w-4" />,
  "Storage Exposure": <HardDrive className="h-4 w-4" />,
  "Admin Protection": <Lock className="h-4 w-4" />,
  "Email Security": <Mail className="h-4 w-4" />,
  "Secrets Protection": <Key className="h-4 w-4" />,
  "Privacy Architecture": <Shield className="h-4 w-4" />,
};

export function SecurityReportManager() {
  const queryClient = useQueryClient();
  const [selectedReport, setSelectedReport] = useState<SecurityReport | null>(null);
  const [reportFindings, setReportFindings] = useState<Finding[]>([]);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [reportToEmail, setReportToEmail] = useState<SecurityReport | null>(null);

  // Fetch latest report
  const { data: latestReport, isLoading: latestLoading } = useQuery({
    queryKey: ["security_report_latest"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("security_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(1)
        .single();

      if (error && error.code !== "PGRST116") throw error;
      return data as unknown as SecurityReport | null;
    },
  });

  // Fetch report history
  const { data: reportHistory, isLoading: historyLoading } = useQuery({
    queryKey: ["security_report_history"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("security_reports")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      return data as unknown as SecurityReport[];
    },
  });

  // Fetch findings for a specific report
  const fetchFindings = async (reportId: string) => {
    const { data, error } = await supabase
      .from("security_report_findings")
      .select("*")
      .eq("report_id", reportId)
      .order("severity", { ascending: true });

    if (error) throw error;
    return data as Finding[];
  };

  // Run new report mutation
  const runReportMutation = useMutation({
    mutationFn: async () => {
      const response = await supabase.functions.invoke("run-security-report");
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["security_report_latest"] });
      queryClient.invalidateQueries({ queryKey: ["security_report_history"] });
      setReportFindings(data.findings || []);
      toast.success("Security report completed");
    },
    onError: (error) => {
      toast.error(`Report failed: ${error.message}`);
    },
  });

  // Send email mutation
  const sendEmailMutation = useMutation({
    mutationFn: async ({ reportId, recipientEmail }: { reportId: string; recipientEmail: string }) => {
      const response = await supabase.functions.invoke("send-security-report-email", {
        body: { reportId, recipientEmail },
      });
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: () => {
      toast.success("Security report sent successfully!");
      setEmailDialogOpen(false);
      setEmailRecipient("");
      setReportToEmail(null);
    },
    onError: (error) => {
      toast.error(`Failed to send email: ${error.message}`);
    },
  });

  // Load report details
  const handleViewReport = async (report: SecurityReport) => {
    setSelectedReport(report);
    try {
      const findings = await fetchFindings(report.id);
      setReportFindings(findings);
    } catch (error) {
      toast.error("Failed to load report findings");
    }
  };

  // Open email dialog
  const openEmailDialog = (report: SecurityReport) => {
    setReportToEmail(report);
    setEmailDialogOpen(true);
  };

  // Send email
  const handleSendEmail = () => {
    if (!reportToEmail || !emailRecipient) return;
    sendEmailMutation.mutate({ reportId: reportToEmail.id, recipientEmail: emailRecipient });
  };

  // Export functions
  const exportJSON = (report: SecurityReport, findings: Finding[]) => {
    const exportData = {
      report: {
        id: report.id,
        created_at: report.created_at,
        app_env: report.app_env,
        status: report.status,
      },
      summary: report.summary,
      findings: findings.map(f => ({
        severity: f.severity,
        category: f.category,
        title: f.title,
        evidence: f.evidence,
        recommendation: f.recommendation,
      })),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eab-security-report-${format(new Date(report.created_at), "yyyy-MM-dd-HHmm")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (rating: string) => {
    switch (rating) {
      case 'green': return BRAND_GREEN;
      case 'yellow': return BRAND_ORANGE;
      case 'red': return BRAND_DARK_ORANGE;
      default: return '#6B7280';
    }
  };

  const getSeverityStyles = (severity: string) => {
    switch (severity) {
      case 'critical': return { bg: BRAND_DARK_ORANGE, text: 'white' };
      case 'high': return { bg: BRAND_DARK_ORANGE, text: 'white' };
      case 'medium': return { bg: BRAND_ORANGE, text: 'black' };
      case 'low': return { bg: BRAND_LIGHT_BLUE, text: 'white' };
      default: return { bg: '#6B7280', text: 'white' };
    }
  };

  const exportPDF = (report: SecurityReport, findings: Finding[]) => {
    const formatDateStr = (dateStr: string) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // Generate professional HTML for PDF
    const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>EAB Connect Security Report - ${formatDateStr(report.created_at)}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      color: #1a1a2e;
      line-height: 1.6;
      background: #f8fafc;
    }
    
    .report-container {
      max-width: 800px;
      margin: 0 auto;
      background: white;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    
    .header {
      background: linear-gradient(135deg, ${BRAND_DARK_BLUE} 0%, ${BRAND_LIGHT_BLUE} 100%);
      color: white;
      padding: 40px;
      position: relative;
    }
    
    .header-content {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    
    .logo-section img {
      height: 50px;
      margin-bottom: 16px;
    }
    
    .header-title {
      font-size: 28px;
      font-weight: 700;
      margin-bottom: 8px;
    }
    
    .header-subtitle {
      font-size: 14px;
      opacity: 0.9;
    }
    
    .report-badge {
      background: rgba(255,255,255,0.2);
      padding: 8px 16px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    
    .meta-bar {
      background: #f1f5f9;
      padding: 16px 40px;
      display: flex;
      justify-content: space-between;
      border-bottom: 1px solid #e2e8f0;
      font-size: 13px;
      color: #64748b;
    }
    
    .meta-item { display: flex; align-items: center; gap: 8px; }
    .meta-label { font-weight: 600; color: #475569; }
    
    .content { padding: 40px; }
    
    .risk-summary {
      background: linear-gradient(to right, #f8fafc, #fff);
      border: 2px solid ${getRiskColor(report.summary.risk_rating)};
      border-radius: 12px;
      padding: 24px;
      margin-bottom: 32px;
      display: flex;
      align-items: center;
      gap: 24px;
    }
    
    .risk-indicator {
      width: 80px;
      height: 80px;
      border-radius: 50%;
      background: ${getRiskColor(report.summary.risk_rating)};
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 24px;
      font-weight: 700;
      flex-shrink: 0;
    }
    
    .risk-details h2 {
      font-size: 24px;
      color: ${getRiskColor(report.summary.risk_rating)};
      margin-bottom: 4px;
    }
    
    .risk-details p { color: #64748b; font-size: 14px; }
    
    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 16px;
      margin-bottom: 32px;
    }
    
    .stat-card {
      text-align: center;
      padding: 20px;
      border-radius: 8px;
      border: 1px solid #e2e8f0;
    }
    
    .stat-card.critical { border-left: 4px solid #EF4444; }
    .stat-card.high { border-left: 4px solid ${BRAND_DARK_ORANGE}; }
    .stat-card.medium { border-left: 4px solid ${BRAND_ORANGE}; }
    .stat-card.low { border-left: 4px solid ${BRAND_LIGHT_BLUE}; }
    
    .stat-number { font-size: 32px; font-weight: 700; margin-bottom: 4px; }
    
    .stat-card.critical .stat-number { color: #EF4444; }
    .stat-card.high .stat-number { color: ${BRAND_DARK_ORANGE}; }
    .stat-card.medium .stat-number { color: ${BRAND_ORANGE}; }
    .stat-card.low .stat-number { color: ${BRAND_LIGHT_BLUE}; }
    
    .stat-label {
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #64748b;
      font-weight: 600;
    }
    
    .section-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 20px;
      padding-bottom: 12px;
      border-bottom: 2px solid ${BRAND_DARK_BLUE};
    }
    
    .section-header h3 {
      font-size: 18px;
      font-weight: 600;
      color: ${BRAND_DARK_BLUE};
    }
    
    .category-summary {
      background: #f8fafc;
      border-radius: 8px;
      padding: 20px;
      margin-bottom: 32px;
    }
    
    .category-grid { display: flex; flex-wrap: wrap; gap: 8px; }
    
    .category-chip {
      background: white;
      border: 1px solid #e2e8f0;
      padding: 8px 14px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }
    
    .category-count {
      background: ${BRAND_LIGHT_BLUE};
      color: white;
      padding: 2px 8px;
      border-radius: 10px;
      margin-left: 6px;
      font-size: 11px;
    }
    
    .findings-list { margin-bottom: 32px; }
    
    .finding-item {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      margin-bottom: 12px;
      overflow: hidden;
    }
    
    .finding-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 16px;
      background: #f8fafc;
      border-bottom: 1px solid #e2e8f0;
    }
    
    .severity-badge {
      padding: 4px 12px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .category-tag {
      font-size: 12px;
      color: #64748b;
      background: white;
      padding: 4px 10px;
      border-radius: 4px;
      border: 1px solid #e2e8f0;
    }
    
    .finding-title { flex: 1; font-weight: 600; font-size: 14px; }
    .finding-body { padding: 16px; }
    
    .finding-evidence {
      background: #f8fafc;
      border-radius: 4px;
      padding: 12px;
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 11px;
      color: #475569;
      margin-bottom: 12px;
      overflow-x: auto;
      white-space: pre-wrap;
      word-break: break-word;
    }
    
    .finding-recommendation {
      font-size: 13px;
      color: #334155;
      padding-left: 16px;
      border-left: 3px solid ${BRAND_GREEN};
    }
    
    .finding-recommendation strong { color: ${BRAND_DARK_GREEN}; }
    
    .footer {
      background: #1a1a2e;
      color: white;
      padding: 32px 40px;
      text-align: center;
    }
    
    .footer-text { font-size: 12px; opacity: 0.7; margin-bottom: 8px; }
    
    .footer-disclaimer {
      font-size: 11px;
      opacity: 0.5;
      max-width: 500px;
      margin: 0 auto;
    }
    
    .report-id {
      font-family: 'Monaco', 'Consolas', monospace;
      font-size: 10px;
      opacity: 0.5;
      margin-top: 16px;
    }
    
    @media print {
      body { background: white; }
      .report-container { box-shadow: none; }
      .finding-item { break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="report-container">
    <div class="header">
      <div class="header-content">
        <div class="logo-section">
          <img src="${eabLogoHorizontal}" alt="EAB Logo" onerror="this.style.display='none'" />
          <div class="header-title">🛡️ Security & Privacy Report</div>
          <div class="header-subtitle">EAB Connect Platform Security Scan</div>
        </div>
        <div class="report-badge">Official Audit</div>
      </div>
    </div>
    
    <div class="meta-bar">
      <div class="meta-item">
        <span class="meta-label">Generated:</span>
        <span>${formatDateStr(report.created_at)}</span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Environment:</span>
        <span>${report.app_env}</span>
      </div>
      <div class="meta-item">
        <span class="meta-label">Status:</span>
        <span>${report.status.toUpperCase()}</span>
      </div>
    </div>
    
    <div class="content">
      <div class="risk-summary">
        <div class="risk-indicator">
          ${report.summary.risk_rating === 'green' ? '✓' : report.summary.risk_rating === 'yellow' ? '!' : '✗'}
        </div>
        <div class="risk-details">
          <h2>${report.summary.risk_rating.toUpperCase()} RISK</h2>
          <p>${report.summary.total_findings} security findings detected across ${Object.keys(report.summary.by_category).length} categories</p>
        </div>
      </div>
      
      <div class="stats-grid">
        <div class="stat-card critical">
          <div class="stat-number">${report.summary.by_severity.critical}</div>
          <div class="stat-label">Critical</div>
        </div>
        <div class="stat-card high">
          <div class="stat-number">${report.summary.by_severity.high}</div>
          <div class="stat-label">High</div>
        </div>
        <div class="stat-card medium">
          <div class="stat-number">${report.summary.by_severity.medium}</div>
          <div class="stat-label">Medium</div>
        </div>
        <div class="stat-card low">
          <div class="stat-number">${report.summary.by_severity.low}</div>
          <div class="stat-label">Low</div>
        </div>
      </div>
      
      <div class="category-summary">
        <div class="section-header">
          <h3>📊 Categories Overview</h3>
        </div>
        <div class="category-grid">
          ${Object.entries(report.summary.by_category).map(([cat, count]) => `
            <div class="category-chip">${cat}<span class="category-count">${count}</span></div>
          `).join('')}
        </div>
      </div>
      
      <div class="section-header">
        <h3>🔍 Detailed Findings</h3>
      </div>
      
      <div class="findings-list">
        ${findings.map(f => {
          const styles = getSeverityStyles(f.severity);
          return `
          <div class="finding-item">
            <div class="finding-header">
              <span class="severity-badge" style="background: ${styles.bg}; color: ${styles.text};">
                ${f.severity.toUpperCase()}
              </span>
              <span class="category-tag">${f.category}</span>
              <span class="finding-title">${f.title}</span>
            </div>
            <div class="finding-body">
              <div class="finding-evidence">${JSON.stringify(f.evidence, null, 2)}</div>
              <div class="finding-recommendation">
                <strong>Recommendation:</strong> ${f.recommendation}
              </div>
            </div>
          </div>
        `}).join('')}
      </div>
    </div>
    
    <div class="footer">
      <div style="margin-bottom: 16px;">
        <strong style="font-size: 18px;">Entrepreneurs Across Borders</strong>
      </div>
      <div class="footer-text">EAB Connect Security & Privacy Audit</div>
      <div class="footer-disclaimer">
        This report contains no personally identifiable information (PII). 
        All findings are based on schema and policy analysis only.
      </div>
      <div class="report-id">Report ID: ${report.id}</div>
    </div>
  </div>
</body>
</html>
    `;

    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eab-security-report-${format(new Date(report.created_at), "yyyy-MM-dd-HHmm")}.html`;
    a.click();
    URL.revokeObjectURL(url);
    toast.info("Report downloaded. Open in browser and print to PDF.");
  };

  const getRiskIcon = (rating: string) => {
    switch (rating) {
      case "green": return <ShieldCheck className="h-8 w-8 text-green-500" />;
      case "yellow": return <ShieldAlert className="h-8 w-8 text-yellow-500" />;
      case "red": return <ShieldX className="h-8 w-8 text-red-500" />;
      default: return <Shield className="h-8 w-8 text-muted-foreground" />;
    }
  };

  const currentReport = selectedReport || latestReport;
  const currentFindings = selectedReport ? reportFindings : (runReportMutation.data?.findings || []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security & Privacy Report
          </h2>
          <p className="text-muted-foreground">
            Automated security checks for RLS, PII exposure, storage, and more
          </p>
        </div>
        <Button 
          onClick={() => runReportMutation.mutate()} 
          disabled={runReportMutation.isPending}
        >
          {runReportMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Running...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Run New Report
            </>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      {currentReport && currentReport.status === "success" && (
        <div className="grid gap-4 md:grid-cols-4">
          {/* Risk Rating */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Risk Rating</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center gap-3">
              {getRiskIcon(currentReport.summary.risk_rating)}
              <span className={`text-2xl font-bold ${
                currentReport.summary.risk_rating === "green" ? "text-green-500" :
                currentReport.summary.risk_rating === "yellow" ? "text-yellow-500" : "text-red-500"
              }`}>
                {currentReport.summary.risk_rating.toUpperCase()}
              </span>
            </CardContent>
          </Card>

          {/* Critical/High */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Critical & High</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{currentReport.summary.by_severity.critical}</p>
                  <p className="text-xs text-muted-foreground">Critical</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-orange-500">{currentReport.summary.by_severity.high}</p>
                  <p className="text-xs text-muted-foreground">High</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Medium/Low */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Medium & Low</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-500">{currentReport.summary.by_severity.medium}</p>
                  <p className="text-xs text-muted-foreground">Medium</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-500">{currentReport.summary.by_severity.low}</p>
                  <p className="text-xs text-muted-foreground">Low</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Findings</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{currentReport.summary.total_findings}</p>
              <p className="text-xs text-muted-foreground">
                Last run: {format(new Date(currentReport.created_at), "PPp")}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Category Breakdown */}
      {currentReport && currentReport.status === "success" && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Findings by Category</span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => exportJSON(currentReport, currentFindings)}
                >
                  <FileJson className="h-4 w-4 mr-1" />
                  JSON
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => exportPDF(currentReport, currentFindings)}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => openEmailDialog(currentReport)}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Email
                </Button>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(currentReport.summary.by_category).map(([category, count]) => (
                <Badge key={category} variant="secondary" className="flex items-center gap-1 px-3 py-1">
                  {categoryIcons[category] || <Shield className="h-4 w-4" />}
                  {category}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Findings Table */}
      {currentFindings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Detailed Findings</CardTitle>
            <CardDescription>
              Review each finding and follow recommendations to improve security
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Severity</TableHead>
                    <TableHead className="w-[150px]">Category</TableHead>
                    <TableHead>Finding</TableHead>
                    <TableHead className="w-[300px]">Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentFindings.map((finding, idx) => (
                    <TableRow key={finding.id || idx}>
                      <TableCell>
                        <Badge className={severityColors[finding.severity]}>
                          {finding.severity.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {categoryIcons[finding.category]}
                          <span className="text-sm">{finding.category}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="font-medium">{finding.title}</p>
                        <pre className="text-xs text-muted-foreground mt-1 max-w-md overflow-x-auto">
                          {JSON.stringify(finding.evidence, null, 2)}
                        </pre>
                      </TableCell>
                      <TableCell className="text-sm">{finding.recommendation}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}

      {/* No Report State */}
      {!currentReport && !latestLoading && !runReportMutation.isPending && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription>
            No security reports have been run yet. Click "Run New Report" to perform your first security audit.
          </AlertDescription>
        </Alert>
      )}

      {/* Report History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Report History
          </CardTitle>
          <CardDescription>Previous security reports (last 20)</CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : reportHistory && reportHistory.length > 0 ? (
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {reportHistory.map((report) => (
                  <div 
                    key={report.id} 
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                    onClick={() => handleViewReport(report)}
                  >
                    <div className="flex items-center gap-3">
                      {report.status === "success" ? (
                        getRiskIcon(report.summary.risk_rating)
                      ) : (
                        <ShieldX className="h-5 w-5 text-destructive" />
                      )}
                      <div>
                        <p className="font-medium">
                          {format(new Date(report.created_at), "PPP")}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {report.status === "success" 
                            ? `${report.summary.total_findings} findings`
                            : "Failed"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {report.status === "success" && (
                        <>
                          <Badge variant="outline">
                            {report.summary.by_severity.critical} critical
                          </Badge>
                          <Badge variant="outline">
                            {report.summary.by_severity.high} high
                          </Badge>
                        </>
                      )}
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewReport(report);
                        }}
                      >
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          ) : (
            <p className="text-center text-muted-foreground py-8">No report history</p>
          )}
        </CardContent>
      </Card>

      {/* Report Detail Dialog */}
      <Dialog open={!!selectedReport} onOpenChange={() => setSelectedReport(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Security Report - {selectedReport && format(new Date(selectedReport.created_at), "PPP")}
            </DialogTitle>
          </DialogHeader>
          
          {selectedReport && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {getRiskIcon(selectedReport.summary.risk_rating)}
                  <span className="text-xl font-bold">
                    {selectedReport.summary.risk_rating.toUpperCase()} RISK
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportJSON(selectedReport, reportFindings)}
                  >
                    <FileJson className="h-4 w-4 mr-1" />
                    JSON
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportPDF(selectedReport, reportFindings)}
                  >
                    <FileText className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => openEmailDialog(selectedReport)}
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Email
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                {["critical", "high", "medium", "low"].map((sev) => (
                  <div key={sev} className="text-center p-3 border rounded-lg">
                    <p className="text-2xl font-bold">
                      {selectedReport.summary.by_severity[sev as keyof typeof selectedReport.summary.by_severity]}
                    </p>
                    <p className="text-sm text-muted-foreground capitalize">{sev}</p>
                  </div>
                ))}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Finding</TableHead>
                    <TableHead>Recommendation</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportFindings.map((finding, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <Badge className={severityColors[finding.severity]}>
                          {finding.severity.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>{finding.category}</TableCell>
                      <TableCell>
                        <p className="font-medium">{finding.title}</p>
                        <pre className="text-xs text-muted-foreground mt-1">
                          {JSON.stringify(finding.evidence, null, 2)}
                        </pre>
                      </TableCell>
                      <TableCell className="text-sm">{finding.recommendation}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" />
              Send Security Report
            </DialogTitle>
            <DialogDescription>
              Send a professionally formatted security report to any email address.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="recipient-email">Recipient Email</Label>
              <Input
                id="recipient-email"
                type="email"
                placeholder="recipient@example.com"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
              />
            </div>
            {reportToEmail && (
              <div className="bg-muted p-3 rounded-lg text-sm">
                <p className="font-medium">Report Details:</p>
                <p className="text-muted-foreground">
                  Date: {format(new Date(reportToEmail.created_at), "PPP")}
                </p>
                <p className="text-muted-foreground">
                  Risk: {reportToEmail.summary.risk_rating.toUpperCase()} • 
                  {reportToEmail.summary.total_findings} findings
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendEmail}
              disabled={!emailRecipient || sendEmailMutation.isPending}
            >
              {sendEmailMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}